'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';

type NotifCategory = 'booking' | 'message' | 'review' | 'payment' | 'system' | 'promo';

interface Notification {
  id: number;
  category: NotifCategory;
  title: string;
  body: string;
  time: string;
  read: boolean;
  actionLabel?: string;
  actionHref?: string;
  icon: string;
}

const initialNotifications: Notification[] = [
  { id: 1, category: 'booking', title: 'New Booking Confirmed', body: 'James Ssekandi confirmed your booking at Luxury Villa Kololo for Aug 25–30.', time: '2 min ago', read: false, icon: '📅', actionLabel: 'View Booking', actionHref: '/booking-confirmation' },
  { id: 2, category: 'message', title: 'New Message from Grace', body: 'Grace Achieng: "Your booking is confirmed. See you soon at Lakeside Retreat!"', time: '15 min ago', read: false, icon: '💬', actionLabel: 'Reply', actionHref: '/messages' },
  { id: 3, category: 'payment', title: 'Payment Received', body: 'UGX 450,000 has been credited to your account for the Kampala Apartment booking.', time: '1 hr ago', read: false, icon: '💰', actionLabel: 'View Payout', actionHref: '/host-dashboard' },
  { id: 4, category: 'review', title: 'New Review Posted', body: 'Sarah Nakato left a 5-star review for Safari Lodge Murchison: "Absolutely breathtaking!"', time: '3 hrs ago', read: true, icon: '⭐', actionLabel: 'View Review', actionHref: '/host-dashboard' },
  { id: 5, category: 'booking', title: 'Booking Request Pending', body: 'You have a new booking request from Peter Mugisha for Bwindi Lodge, Sep 10–14.', time: '5 hrs ago', read: false, icon: '🔔', actionLabel: 'Respond', actionHref: '/host-dashboard' },
  { id: 6, category: 'system', title: 'Profile Verification Complete', body: 'Your host profile has been verified. Your listings are now eligible for Superhost status.', time: 'Yesterday', read: true, icon: '✅' },
  { id: 7, category: 'promo', title: 'Boost Your Listing', body: 'Properties with professional photos get 40% more bookings. Upload high-quality photos today.', time: 'Yesterday', read: true, icon: '🚀', actionLabel: 'Manage Photos', actionHref: '/host-dashboard' },
  { id: 8, category: 'message', title: 'Message from David', body: 'David Ochieng: "Hope you enjoyed your stay! Please leave a review when you get a chance."', time: '2 days ago', read: true, icon: '💬', actionLabel: 'Reply', actionHref: '/messages' },
  { id: 9, category: 'payment', title: 'Payout Processed', body: 'Your payout of UGX 1,200,000 has been sent to your registered bank account.', time: '3 days ago', read: true, icon: '🏦' },
  { id: 10, category: 'system', title: 'New Platform Feature', body: 'Availability management is now live! Block dates and set open ranges directly from your dashboard.', time: '4 days ago', read: true, icon: '🆕', actionLabel: 'Try It', actionHref: '/host-dashboard' },
  { id: 11, category: 'review', title: 'Review Response Needed', body: 'Moses Kato left a 1-star review for City Apartment. Consider responding to address their concerns.', time: '5 days ago', read: true, icon: '⚠️', actionLabel: 'Respond', actionHref: '/host-dashboard' },
  { id: 12, category: 'promo', title: 'Weekend Deal Opportunity', body: 'Add a weekend discount to your listings and attract more last-minute bookings this season.', time: '1 week ago', read: true, icon: '🎯', actionLabel: 'Add Deal', actionHref: '/special-offers' },
];

const categoryConfig: Record<NotifCategory, { label: string; color: string; bg: string; border: string }> = {
  booking: { label: 'Bookings', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
  message: { label: 'Messages', color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200' },
  review: { label: 'Reviews', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
  payment: { label: 'Payments', color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' },
  system: { label: 'System', color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200' },
  promo: { label: 'Promotions', color: 'text-[#4088C6]', bg: 'bg-[#4088C6]/10', border: 'border-[#4088C6]/20' },
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>(initialNotifications);
  const [activeCategory, setActiveCategory] = useState<NotifCategory | 'all'>('all');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const markRead = (id: number) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const deleteNotif = (id: number) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const filtered = notifications
    .filter(n => activeCategory === 'all' || n.category === activeCategory)
    .filter(n => !showUnreadOnly || !n.read);

  const categoryCounts = Object.keys(categoryConfig).reduce((acc, cat) => {
    acc[cat as NotifCategory] = notifications.filter(n => n.category === cat && !n.read).length;
    return acc;
  }, {} as Record<NotifCategory, number>);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {/* Page Header */}
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                Notifications
              </h1>
              <p className="text-gray-500 mt-1 text-sm">
                {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-sm font-semibold text-[#4088C6] hover:text-[#3070a6] transition-colors"
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* Category Filter Pills */}
          <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
            <button
              onClick={() => setActiveCategory('all')}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                activeCategory === 'all' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-400'
              }`}
            >
              All
              {unreadCount > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${activeCategory === 'all' ? 'bg-white/20 text-white' : 'bg-[#4088C6]/10 text-[#4088C6]'}`}>
                  {unreadCount}
                </span>
              )}
            </button>
            {(Object.entries(categoryConfig) as [NotifCategory, typeof categoryConfig[NotifCategory]][]).map(([cat, cfg]) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                  activeCategory === cat ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-400'
                }`}
              >
                {cfg.label}
                {categoryCounts[cat] > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${activeCategory === cat ? 'bg-white/20 text-white' : 'bg-[#4088C6]/10 text-[#4088C6]'}`}>
                    {categoryCounts[cat]}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Unread Toggle */}
          <div className="flex items-center justify-between mb-5">
            <p className="text-sm text-gray-500">{filtered.length} notification{filtered.length !== 1 ? 's' : ''}</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-sm font-medium text-gray-600">Unread only</span>
              <div
                onClick={() => setShowUnreadOnly(v => !v)}
                className={`relative w-10 h-5.5 rounded-full transition-colors cursor-pointer ${showUnreadOnly ? 'bg-[#4088C6]' : 'bg-gray-200'}`}
                style={{ height: '22px', width: '40px' }}
              >
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${showUnreadOnly ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
            </label>
          </div>

          {/* Notifications List */}
          {filtered.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <div className="text-5xl mb-4">🔔</div>
              <p className="text-lg font-bold text-gray-700">No notifications</p>
              <p className="text-sm text-gray-400 mt-1">
                {showUnreadOnly ? 'No unread notifications in this category' : 'You\'re all caught up!'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((notif) => {
                const cfg = categoryConfig[notif.category];
                return (
                  <div
                    key={notif.id}
                    className={`group relative bg-white rounded-2xl border transition-all hover:shadow-md ${
                      !notif.read ? 'border-l-4 border-l-[#4088C6] border-gray-100' : 'border-gray-100'
                    }`}
                  >
                    <div className="flex items-start gap-4 p-4">
                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0 ${cfg.bg} border ${cfg.border}`}>
                        {notif.icon}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-0.5">
                              <p className={`text-sm font-bold ${notif.read ? 'text-gray-700' : 'text-gray-900'}`}>
                                {notif.title}
                              </p>
                              {!notif.read && (
                                <div className="w-2 h-2 bg-[#4088C6] rounded-full flex-shrink-0" />
                              )}
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
                                {cfg.label}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 leading-relaxed">{notif.body}</p>
                            <div className="flex items-center gap-3 mt-2">
                              <span className="text-xs text-gray-400">{notif.time}</span>
                              {notif.actionLabel && notif.actionHref && (
                                <a
                                  href={notif.actionHref}
                                  onClick={() => markRead(notif.id)}
                                  className={`text-xs font-semibold ${cfg.color} hover:underline transition-colors`}
                                >
                                  {notif.actionLabel} →
                                </a>
                              )}
                              {!notif.read && (
                                <button
                                  onClick={() => markRead(notif.id)}
                                  className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                  Mark read
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Delete */}
                          <button
                            onClick={() => deleteNotif(notif.id)}
                            className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-red-500 transition-all flex-shrink-0"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
